import { useEffect, useRef } from 'react';

interface UseInfiniteScrollOptions {
  onLoadMore: () => void | Promise<void>;
  hasMore: boolean;
  loading: boolean;
  threshold?: number; // Intersection threshold (0-1, default 0.1 = 10% visible)
  rootMargin?: string; // Margin around root (default '100px' to trigger early)
}

/**
 * Hook for implementing infinite scroll using IntersectionObserver
 * More performant than scroll listeners - uses native browser API
 *
 * Usage:
 * const loaderRef = useInfiniteScroll({
 *   onLoadMore: handleLoadMore,
 *   hasMore: hasMore,
 *   loading: loading || loadingMore
 * });
 *
 * Then add the ref to an element at the bottom of your list:
 * <div ref={loaderRef} />
 */
export const useInfiniteScroll = ({
  onLoadMore,
  hasMore,
  loading,
  threshold = 0.1,
  rootMargin = '100px'
}: UseInfiniteScrollOptions) => {
  const loaderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Create observer
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        // Trigger load when the loader element becomes visible
        if (first.isIntersecting && hasMore && !loading) {
          onLoadMore();
        }
      },
      {
        threshold,
        rootMargin // Load content slightly before reaching the element
      }
    );

    const currentLoader = loaderRef.current;
    if (currentLoader) {
      observer.observe(currentLoader);
    }

    // Cleanup
    return () => {
      if (currentLoader) {
        observer.unobserve(currentLoader);
      }
    };
  }, [hasMore, loading, onLoadMore, threshold, rootMargin]);

  return loaderRef;
};
